import { Payment } from '../../domain/entities/Payment';
import { PaymentRepository } from '../../domain/repositories/PaymentRepository';
import { ApiClient } from '../api/ApiClient';

/**
 * Implementación del repositorio de pagos que se conecta a la API
 */
export class ApiPaymentRepository implements PaymentRepository {
  private apiClient: ApiClient;

  constructor() {
    this.apiClient = new ApiClient();
  }

  /**
   * Crea un nuevo registro de pago
   * @param payment - Datos del pago a crear
   * @returns Promesa que resuelve al pago creado
   */
  async create(payment: Omit<Payment, 'id'>): Promise<Payment> {
    try {
      const response = await this.apiClient.post<Payment>('/payments', payment);
      return response;
    } catch (error) {
      console.error('Error al crear pago:', error);
      throw new Error('Error al crear el pago');
    }
  }

  /**
   * Obtiene un pago por su ID
   * @param id - ID del pago a buscar
   * @returns Promesa que resuelve al pago encontrado, o null si no existe
   */
  async getById(id: string): Promise<Payment | null> {
    try {
      const response = await this.apiClient.get<Payment>(`/payments/${id}`);
      return response;
    } catch (error) {
      console.error(`Error al obtener pago con ID ${id}:`, error);
      return null;
    }
  }

  /**
   * Actualiza el estado de un pago
   * @param id - ID del pago a actualizar
   * @param status - Nuevo estado del pago
   * @returns Promesa que resuelve al pago actualizado
   */
  async updateStatus(id: string, status: Payment['status']): Promise<Payment> {
    try {
      const response = await this.apiClient.patch<Payment>(`/payments/${id}/status`, { status });
      return response;
    } catch (error) {
      console.error(`Error al actualizar estado del pago con ID ${id}:`, error);
      throw new Error(`Error al actualizar estado del pago con ID ${id}`);
    }
  }

  /**
   * Obtiene todos los pagos asociados a una factura
   * @param invoiceId - ID de la factura
   * @returns Promesa que resuelve a un array de pagos
   */
  async getByInvoiceId(invoiceId: string): Promise<Payment[]> {
    try {
      const response = await this.apiClient.get<Payment[]>(`/payments/invoice/${invoiceId}`);
      return response;
    } catch (error) {
      console.error(`Error al obtener pagos para la factura con ID ${invoiceId}:`, error);
      return [];
    }
  }

  /**
   * Consulta el estado actual de un pago por su referencia de transacción
   * @param transactionRef - Referencia de la transacción
   * @returns Promesa que resuelve al estado actual del pago
   */
  async checkPaymentStatus(transactionRef: string): Promise<{ ESTADO: 'Aprobada' | 'Rechazada' | 'Pendiente' }> {
    try {
      const response = await this.apiClient.get<{
        success: boolean;
        data: {
          id: string;
          status: string;
          paymentDate: string;
          amount: number;
          ticketId: string;
        };
      }>(`/ConsultarEstado/${transactionRef}`);

      // Mapear el estado del backend al formato que espera la aplicación
      let estado: 'Aprobada' | 'Rechazada' | 'Pendiente';
      switch (response.data.status) {
        case 'Aprobada':
          estado = 'Aprobada';
          break;
        case 'Rechazada':
          estado = 'Rechazada';
          break;
        case 'UNPAID':
          estado = 'Rechazada';
          break;
        case 'RUNNING':
          estado = 'Pendiente';
          break;
        default:
          estado = 'Pendiente';
          break;
      }

      return { ESTADO: estado };
    } catch (error) {
      console.error(`Error al consultar estado del pago con referencia ${transactionRef}:`, error);
      throw new Error('Error al consultar el estado del pago');
    }
  }

  /**
   * Registra un pago en el backend
   * @param paymentData - Datos del pago a registrar
   * @returns Promesa que resuelve con el ID del pago registrado
   */
  async registerPayment(paymentData: {
    uuid?: string;
    valorPagado: number;
    identificacion: string;
    estado: string;
    fechaPago: string;
    medioPago: string;
    factura: string;
    codigoFideicomiso?: string;
    oficina?: string;
    prefijo?: string;
    numeroFactura?: string;
    nombre?: string;
    detalleFactura?: string;
    fechaVencimiento?: string;
    intereses?: number;
    fechaFactura?: string;
    valorTotal?: number;
    secuencial?: number;
    valorSaldo?: number;
    id?: number;
  }): Promise<{ id: number }> {
    try {
      const response = await this.apiClient.post<{
        success: boolean;
        data: {
          message: string;
          id: number;
        };
        timestamp: string;
      }>('/RegistrarPago', paymentData);
      return { id: response.data.id };
    } catch (error) {
      console.error('Error al registrar el pago:', error);
      throw new Error('Error al registrar el pago en el sistema');
    }
  }
} 